/**
 * Centralized file upload configuration — single source of truth.
 *
 * Used by both frontend and backend so they cannot drift:
 *   - Frontend: `<input accept="...">`, Uppy `allowedFileTypes`, pre-validation
 *   - Backend (`upload-guards.ts`): MIME allow-list + per-type size caps
 *
 * Adding a new file type? Add an entry here. Both layers pick it up automatically.
 */

const MB = 1024 * 1024;

// ---------------------------------------------------------------------------
// Type definitions
// ---------------------------------------------------------------------------

export interface MimeTypeConfig {
  /** Allowed file extensions for this MIME (with leading dot, lowercase). */
  extensions: string[];
  /** Max upload size in bytes for this MIME. */
  maxBytes: number;
}

// ---------------------------------------------------------------------------
// Allowed MIME types → extensions + per-type size cap
// ---------------------------------------------------------------------------
//
// Caps are tuned to ~10× the 99th-percentile real-world file size for that
// type. Adjust based on observed rejection logs if a category turns out too
// tight in production.
//
// Archives (.zip, .tar.gz, .7z, .rar) are intentionally excluded — opaque
// containers can hide executables/scripts under any plausible MIME. Add a
// specific archive MIME with a reasonable cap only when a concrete product
// use case appears.

export const ALLOWED_MIME_TYPES: Record<string, MimeTypeConfig> = {
  // Images
  'image/jpeg':                    { extensions: ['.jpg', '.jpeg'], maxBytes: 15 * MB },
  'image/png':                     { extensions: ['.png'],          maxBytes: 15 * MB },
  'image/gif':                     { extensions: ['.gif'],          maxBytes: 15 * MB },
  'image/webp':                    { extensions: ['.webp'],         maxBytes: 15 * MB },
  'image/svg+xml':                 { extensions: ['.svg'],          maxBytes: 5  * MB },
  'image/x-icon':                  { extensions: ['.ico'],          maxBytes: 1  * MB },
  'image/vnd.microsoft.icon':      { extensions: ['.ico'],          maxBytes: 1  * MB },

  // Fonts
  'font/otf':                      { extensions: ['.otf'],          maxBytes: 5 * MB },
  'application/vnd.ms-opentype':   { extensions: ['.otf'],          maxBytes: 5 * MB },
  'font/ttf':                      { extensions: ['.ttf'],          maxBytes: 5 * MB },
  'application/x-font-ttf':        { extensions: ['.ttf'],          maxBytes: 5 * MB },
  'font/woff':                     { extensions: ['.woff'],         maxBytes: 5 * MB },
  'application/font-woff':         { extensions: ['.woff'],         maxBytes: 5 * MB },
  'font/woff2':                    { extensions: ['.woff2'],        maxBytes: 5 * MB },

  // Documents
  'application/pdf':                                                              { extensions: ['.pdf'],          maxBytes: 25 * MB },
  'text/plain':                                                                   { extensions: ['.txt'],          maxBytes: 10 * MB },
  'text/markdown':                                                                { extensions: ['.md'],           maxBytes: 5  * MB },
  'text/html':                                                                    { extensions: ['.html', '.htm'], maxBytes: 5  * MB },
  'application/msword':                                                           { extensions: ['.doc'],          maxBytes: 25 * MB },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':      { extensions: ['.docx'],         maxBytes: 25 * MB },
  'application/rtf':                                                              { extensions: ['.rtf'],          maxBytes: 5  * MB },

  // Presentations
  'application/vnd.ms-powerpoint':                                                { extensions: ['.ppt'],          maxBytes: 50 * MB },
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':    { extensions: ['.pptx'],         maxBytes: 50 * MB },

  // Spreadsheets
  'application/vnd.ms-excel':                                                     { extensions: ['.xls'],          maxBytes: 25 * MB },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':            { extensions: ['.xlsx'],         maxBytes: 25 * MB },

  // Data
  'application/json':              { extensions: ['.json'],         maxBytes: 10 * MB },
  'text/csv':                      { extensions: ['.csv'],          maxBytes: 25 * MB },
  'text/tab-separated-values':     { extensions: ['.tsv'],          maxBytes: 25 * MB },
  'text/xml':                      { extensions: ['.xml'],          maxBytes: 10 * MB },
  'application/xml':               { extensions: ['.xml'],          maxBytes: 10 * MB },
  'text/yaml':                     { extensions: ['.yaml', '.yml'], maxBytes: 5  * MB },
  'application/x-yaml':            { extensions: ['.yaml', '.yml'], maxBytes: 5  * MB },

  // Audio
  'audio/mpeg':                    { extensions: ['.mp3'],          maxBytes: 50 * MB },
  'audio/wav':                     { extensions: ['.wav'],          maxBytes: 50 * MB },
  'audio/ogg':                     { extensions: ['.ogg'],          maxBytes: 50 * MB },
  'audio/aac':                     { extensions: ['.aac'],          maxBytes: 50 * MB },
  'audio/flac':                    { extensions: ['.flac'],         maxBytes: 50 * MB },
  'audio/mp4':                     { extensions: ['.m4a'],          maxBytes: 50 * MB },
  'audio/webm':                    { extensions: ['.weba'],         maxBytes: 50 * MB },

  // Video
  'video/mp4':                     { extensions: ['.mp4'],          maxBytes: 100 * MB },
  'video/webm':                    { extensions: ['.webm'],         maxBytes: 100 * MB },
  'video/ogg':                     { extensions: ['.ogv'],          maxBytes: 100 * MB },
  'video/quicktime':               { extensions: ['.mov'],          maxBytes: 100 * MB },
  'video/x-msvideo':               { extensions: ['.avi'],          maxBytes: 100 * MB },

  // Code / web
  'text/css':                      { extensions: ['.css'],          maxBytes: 2  * MB },
  'application/wasm':              { extensions: ['.wasm'],         maxBytes: 10 * MB },
};

// ---------------------------------------------------------------------------
// Blocked extensions — dangerous file types that must never be uploaded
// ---------------------------------------------------------------------------

export const BLOCKED_EXTENSIONS: Set<string> = new Set([
  // Executables
  '.exe', '.dll', '.bat', '.cmd', '.com', '.msi', '.scr', '.cpl',
  // Scripts
  '.ps1', '.sh', '.bash', '.vbs', '.js', '.py', '.rb', '.php', '.jsp', '.asp', '.aspx',
  '.ws', '.wsf',
  // Java
  '.jar',
  // Windows config
  '.inf', '.reg',
  // Office macros
  '.docm', '.xlsm', '.pptm',
]);

// ---------------------------------------------------------------------------
// Flat size constants (legacy — for client-side coarse limits)
// ---------------------------------------------------------------------------
//
// `MAX_FILE_SIZE_MB` / `MAX_FILE_SIZE_BYTES` are the *upper bound* across all
// MIME types — used by client-side libraries (Uppy `maxFileSize`, FileUpload
// component) that enforce a single flat cap. The authoritative per-type cap
// lives in `ALLOWED_MIME_TYPES[mime].maxBytes` and is enforced server-side.

const MAX_PER_TYPE_BYTES = Math.max(
  ...Object.values(ALLOWED_MIME_TYPES).map((c) => c.maxBytes),
);

export const MAX_FILE_SIZE_BYTES = MAX_PER_TYPE_BYTES;
export const MAX_FILE_SIZE_MB = Math.round(MAX_FILE_SIZE_BYTES / MB);

// ---------------------------------------------------------------------------
// HTML accept attribute (array form for Uppy, string form for <input>)
// ---------------------------------------------------------------------------

function buildUniqueExtensions(): string[] {
  const exts = new Set<string>();
  for (const config of Object.values(ALLOWED_MIME_TYPES)) {
    for (const ext of config.extensions) {
      exts.add(ext);
    }
  }
  return Array.from(exts).sort();
}

/**
 * Array of allowed file type specifiers for Uppy `allowedFileTypes`.
 * Includes wildcard groups (image/*, video/*, audio/*) plus individual extensions.
 */
export const ACCEPT_ARRAY: string[] = [
  'image/*',
  'video/*',
  'audio/*',
  ...buildUniqueExtensions(),
];

/**
 * Comma-separated string for HTML `<input accept="...">`.
 */
export const ACCEPT_ATTRIBUTE: string = ACCEPT_ARRAY.join(',');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalize a client-supplied Content-Type header:
 *   - lowercase
 *   - strip parameters (`image/jpeg; charset=binary` → `image/jpeg`)
 *   - trim whitespace
 *
 * Returns empty string for non-string / nullish input.
 */
export function normalizeMime(raw: string): string {
  if (typeof raw !== 'string') return '';
  const noParams = raw.split(';')[0] ?? '';
  return noParams.trim().toLowerCase();
}

/**
 * Check if a filename has a blocked (dangerous) extension.
 * Matches against the final extension, case-insensitive.
 */
export function isBlockedExtension(filename: string): boolean {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === 0) return false;
  const ext = filename.substring(lastDot).toLowerCase();
  return BLOCKED_EXTENSIONS.has(ext);
}
